import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";

import { type CreativeSphereSearch, SearchSchema } from "./route";
import type { ChannelMedium, OrderBy } from "@/types/dragon-tiles";

import { framePreviewsInfiniteQueryOptions } from "@/api/dragon-tiles";
import { ErrorComponent } from "@/components/ErrorComponent";
import { LoadingFallback } from "@/components/LoadingFallback";
import { FRAMES_PAGE_SIZE } from "@/components/TilesPagination/TilesPagination";
import { VirtualizedGrid } from "@/features/CreativeSphere/components/VirtualizedGrid";
import { VirtualizedTable } from "@/features/CreativeSphere/components/VirtualizedTable";
import { type FrameWithVariantPreviews } from "@/features/CreativeSphere/constants";

type FramePreviewFilters = {
  search?: string;
  medium?: ChannelMedium;
  dragon_tile?: string;
  event?: string;
  order_by?: OrderBy;
  order?: "asc" | "desc";
  is_active?: boolean;
  deleted_only?: boolean;
};

function buildFramePreviewFilters(
  search: CreativeSphereSearch,
): FramePreviewFilters | undefined {
  const filters: FramePreviewFilters = {};
  if (search.search) filters.search = search.search;
  if (search.medium) filters.medium = search.medium;
  if (search.dragon_tile) filters.dragon_tile = search.dragon_tile;
  if (search.event) filters.event = search.event;
  if (search.order_by) filters.order_by = search.order_by;
  if (search.order) filters.order = search.order;
  if (search.deactivated) {
    filters.deleted_only = true;
  } else if (search.is_active !== undefined) {
    filters.is_active = search.is_active;
  }
  return Object.keys(filters).length > 0 ? filters : undefined;
}

/** ================== Route ================== */
export const Route = createFileRoute("/_auth/creative-sphere/$siteId/")({
  validateSearch: (raw): CreativeSphereSearch => SearchSchema.parse(raw),
  loaderDeps: ({ search }) => [search],
  loader: ({ params, context, deps: [search] }) => {
    const { queryClient } = context as {
      queryClient: import("@tanstack/react-query").QueryClient;
    };
    const { siteId } = params;

    void queryClient.prefetchInfiniteQuery(
      framePreviewsInfiniteQueryOptions(
        siteId,
        FRAMES_PAGE_SIZE,
        buildFramePreviewFilters(search),
      ),
    );
  },
  component: RouteComponent,
  pendingComponent: LoadingFallback,
  errorComponent: ErrorComponent,
});

function RouteComponent() {
  const { siteId } = Route.useParams();
  const search = Route.useSearch();
  const isDeactivated = search.deactivated ?? false;

  const pageSize = FRAMES_PAGE_SIZE;

  const filters = useMemo(() => buildFramePreviewFilters(search), [search]);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery(
      framePreviewsInfiniteQueryOptions(siteId, pageSize, filters),
    );

  const displayItems = useMemo(
    (): FrameWithVariantPreviews[] =>
      data?.pages.flatMap((page) =>
        page.results.map((frame) => ({ frame, variantPreviews: {} })),
      ) ?? [],
    [data?.pages],
  );

  const isGridView = (search.view ?? "grid") === "grid";
  const emptyMessage = isDeactivated
    ? "No permanently deactivated tiles"
    : search.event
      ? "No tiles for this event yet"
      : "No tiles found";

  return isGridView ? (
    <VirtualizedGrid
      items={displayItems}
      siteId={siteId}
      filterMedium={search.medium}
      isActionable={!isDeactivated}
      emptyMessage={emptyMessage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      isLoading={isLoading}
    />
  ) : (
    <VirtualizedTable
      items={displayItems}
      siteId={siteId}
      filterMedium={search.medium}
      isActionable={!isDeactivated}
      emptyMessage={emptyMessage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      isLoading={isLoading}
    />
  );
}
